import { useState, useEffect, useRef } from 'react';
import "react-datepicker/dist/react-datepicker.css";
import CalendarInput from '../shared/CalendarInput';
import { toHtmlInputDate, fromHtmlInputDate } from '../../utils/dateUtils';
import { LOAN_PROGRAM_CODES } from '../../config/organizations';
import {
    getLoanProgramOptionsFor,
    isLoanProgram,
    isLoanProgramField,
    toEncompassLoanProgram
} from '../../utils/loanProgramUtil';

// PROGRAM_001 = ALLY, PROGRAM_011 = ALLY EQUITY FIRST. Neither exists in
// SkyTPO's Encompass, so on that org these checks simply never match.
const ALLY_PRODUCTS = [LOAN_PROGRAM_CODES.PROGRAM_001, LOAN_PROGRAM_CODES.PROGRAM_011];
const isAllyProduct = (product) => isLoanProgram(product, ALLY_PRODUCTS);

const AdditionalInfoControl = ({ additionalFields, fieldValues, bpcPrograms, hasLoCompHistory, onFieldChange, lockOccupancy, isCorrespondent, isReadOnly = false }) => {
    const [localFieldValues, setLocalFieldValues] = useState({});
    // track LCP.X1 value so we can enable/disable field 389
    const [loanCompPlan, setLoanCompPlan] = useState(fieldValues?.['LCP.X1'] || '');
    const [forceLcpLenderPaid, setForceLcpLenderPaid] = useState(false);
    const isBorrowerPlan = (val) => typeof val === 'string' && /borrower/i.test(val);
    const fieldsJson = additionalFields;
    // ref to skip the "clear 389" logic during initial load
    const initializedRef = useRef(false);
    const [disable389, setDisable389] = useState(false);
    const [disableLcp, setDisableLcp] = useState(false);
    const [disableLcpBecauseAlly, setDisableLcpBecauseAlly] = useState(false);
    const forceBorrowerPaidBecauseAllyRef = useRef(false);

    useEffect(() => {
        if (!fieldValues) return;

        // build a sanitized map of only the fields we care about
        const sanitized = {};
        for (const field of additionalFields) {
            if (!Object.prototype.hasOwnProperty.call(fieldValues, field.id)) continue;

            let val = fieldValues[field.id];

            // Encompass field 420 returns display values such as "Second Lien".
            // Convert them back to the contract values used by the dropdown.
            if (field.id === "420") {
                const lienPosition = (val ?? "").toString().replace(/\s+/g, "").toLowerCase();

                if (lienPosition === "firstlien" || lienPosition === "first") {
                    val = "FirstLien";
                } else if (lienPosition === "secondlien" || lienPosition === "subordinate") {
                    val = "SecondLien";
                }
            }

            // date normalization
            if (field.datatype === "date" && val === "//") {
                val = "";
            }

            // strip commas for numeric fields (decimal/integer)
            if (field.datatype === "decimal" || field.datatype === "integer") {
                if (val == null) {
                    val = "";
                } else {
                    val = String(val).replace(/,/g, "");
                }
            }


            if (field.format === "YN_BOOL") {
                if (val === "Y") val = "true";
                else if (val === "N") val = "false";
            }

            // always store as string for input binding
            sanitized[field.id] = val == null ? "" : String(val);
        }

        // inject occupancy even though it's not in additionalFields.json
        sanitized["1811"] = fieldValues["1811"] == null ? "" : String(fieldValues["1811"]);
        // (optional) also inject citizenship if you ever move it out of additionalFields
        sanitized["CX.CITIZENSHIP.TYPE"] = fieldValues["CX.CITIZENSHIP.TYPE"] == null ? "" : String(fieldValues["CX.CITIZENSHIP.TYPE"]);


        // Helper: shallow compare two plain objects (string values only here)
        const shallowEqual = (a = {}, b = {}) => {
            const aKeys = Object.keys(a);
            const bKeys = Object.keys(b);
            if (aKeys.length !== bKeys.length) return false;
            for (let i = 0; i < aKeys.length; i++) {
                const k = aKeys[i];
                if (a[k] !== b[k]) return false;
            }
            return true;
        };

        // Only update local state when there is an actual change
        setLocalFieldValues(prev => {
            // merge previous with sanitized so we don't lose other local edits
            const merged = { ...prev, ...sanitized };
            if (shallowEqual(prev, merged)) {
                // no change => return prev (React will skip re-render)
                return prev;
            }
            return merged;
        });

        // set loanCompPlan raw value (used to detect borrower)
        if (Object.prototype.hasOwnProperty.call(fieldValues, 'LCP.X1')) {
            setLoanCompPlan(fieldValues['LCP.X1'] || '');
        }

        const val14 = fieldValues['14'];
        const val19 = fieldValues['19'];
        const val1811 = fieldValues['1811'];  // Occupancy

        const shouldForce =
            val14 === 'TX' &&
            val19 === 'Cash-Out Refinance' &&
            val1811 === 'PrimaryResidence';

        if (shouldForce) {
            setForceLcpLenderPaid(true); // keeps dropdown disabled via disabledBecauseOfForce

            const forcedPlan = "Lender Paid";

            setLocalFieldValues(prev => {
                if (prev["LCP.X1"] === forcedPlan) return prev;
                return { ...prev, "LCP.X1": forcedPlan };
            });

            setLoanCompPlan(forcedPlan);
            onFieldChange?.("LCP.X1", forcedPlan);
        } else {
            setForceLcpLenderPaid(false);
        }


    }, [fieldValues, additionalFields, onFieldChange, hasLoCompHistory]);


    useEffect(() => {
        // Skip during the very first initialization cycle to avoid clearing a value we just loaded.
        if (!initializedRef.current) {
            initializedRef.current = true;
            return;
        }

        const feeFieldId = '389';
        const shouldRequire = isBorrowerPlan(loanCompPlan);

        if (!shouldRequire) {
            // only clear local + notify parent when it actually had a value
            const hadValue = !!localFieldValues[feeFieldId];
            if (!hadValue) {
                // nothing to clear
                return;
            }

            // clear local and notify parent to clear
            setLocalFieldValues(prev => {
                // double-check in updater (defensive)
                if (!prev[feeFieldId]) return prev;
                const next = { ...prev, [feeFieldId]: "" };
                return next;
            });

            if (onFieldChange) onFieldChange(feeFieldId, "");
        }
        // if it becomes borrower-plan we leave existing value alone
    }, [loanCompPlan, localFieldValues, onFieldChange]);

    const handleChange = (e, field) => {
        const { value } = e.target;

        // remove highlight immediately for this field (user fixed it / is trying)
        removeHighlightForField(field.id);

        // update local state so the UI updates instantly
        setLocalFieldValues(prev => ({
            ...prev,
            [field.id]: value
        }));

        // prepare value to send to parent (reverse any display-only transform)
        let valueToSend = value;

        // If field uses YN_BOOL we displayed "true"/"false" — send Y/N upstream
        if (field.format === "YN_BOOL") {
            if (value === "true") valueToSend = "Y";
            else if (value === "false") valueToSend = "N";
            else valueToSend = value; // fallback
        }

        // call parent's handler if provided
        if (onFieldChange) onFieldChange(field.id, valueToSend);
    };


    const getDateValidation = (fieldId) => {
        const def = fieldsJson.find(f => f.id === fieldId);
        return def?.validation || {};
    };
    const getFieldDef = (id) => additionalFields.find(f => f.id === id);

    /*
     * Loan-program options are stored as canonical codes, so the option value
     * stays canonical (that is what every comparison below expects) while the
     * label becomes this org's product name - and products the org does not
     * carry drop out of the list entirely. Every other field is unchanged.
     */
    const normalizeOptions = (opts = [], fieldDef = null) => {
        const isProgramField =
            fieldDef?.valueSet === "loanProgram" || isLoanProgramField(fieldDef?.id);

        if (isProgramField) return getLoanProgramOptionsFor(opts);

        return (opts || []).map(o =>
            typeof o === "string"
                ? { value: o, text: o }
                : { value: String(o.value), text: o.text || o.value }
        );
    };

    const getAllowedIncomeOptionValues = (doc) => {
        const dep = getFieldDef('CX.DOC.TYPE')?.dependentOptions;
        return (dep && doc) ? (dep.map?.[doc] || []).map(String) : [];
    };

    const conditionMatches = (cond, values) => {
        const left = (values?.[cond.field] ?? "").toString().trim();
        if (cond.equals != null) {
            return left === String(cond.equals).trim();
        }
        if (Array.isArray(cond.in)) {
            return cond.in.map(String).includes(left);
        }
        return false;
    };

    const ruleMatches = (ruleIf, values) => {
        if (!ruleIf) return false;

        // AND support: { all: [ {field, equals/in}, ... ] }
        if (Array.isArray(ruleIf.all)) {
            return ruleIf.all.every(c => conditionMatches(c, values));
        }

        // Single condition support: { field, equals } OR { field, in: [...] }
        return conditionMatches(ruleIf, values);
    };

    // Returns { options, readonly } for a field, evaluating optionRules
    const getEffectiveOptionsAndReadonly = (fieldDef, values) => {
        const base = normalizeOptions(fieldDef?.options || [], fieldDef);
        const rules = Array.isArray(fieldDef?.optionRules) ? fieldDef.optionRules : [];
        let readonly = fieldDef?.readonly;
        let options = base;

        for (const r of rules) {
            if (r?.if && ruleMatches(r.if, values) && r?.then) {
                // 1) Replace options
                if (r.then.options) options = normalizeOptions(r.then.options, fieldDef);

                // 2) Add options
                if (Array.isArray(r.then.addOptions)) {
                    const adds = normalizeOptions(r.then.addOptions, fieldDef);
                    const existing = new Set(options.map(o => o.value));
                    options = options.concat(adds.filter(o => !existing.has(o.value)));
                }

                // 3) Override readonly if present
                if (typeof r.then.readonly === "boolean") {
                    readonly = r.then.readonly;
                }
            }
        }
        return { options, readonly };
    };

    // For backward compatibility, keep getEffectiveOptions
    const getEffectiveOptions = (fieldDef, values) => getEffectiveOptionsAndReadonly(fieldDef, values).options;

    const incomeFieldDef = getFieldDef('CX.INCOME.TYPE');
    const allIncomeOptions = getEffectiveOptions(incomeFieldDef, localFieldValues);
    const selectedDocValue = localFieldValues['CX.DOC.TYPE'] || '';
    const allowedIncomeValues = getAllowedIncomeOptionValues(selectedDocValue);
    const filteredIncomeOptions = selectedDocValue === '' ? [] :
        (allowedIncomeValues.length === 0 ? allIncomeOptions : allIncomeOptions.filter(o => allowedIncomeValues.includes(o.value)));

    const initialDocSyncRef = useRef(true);
    useEffect(() => {
        const docVal = localFieldValues['CX.DOC.TYPE'] || '';
        const allowed = getAllowedIncomeOptionValues(docVal) || [];
        const currentIncome = localFieldValues['CX.INCOME.TYPE'] || '';
        const setIncomeValue = (v) => { setLocalFieldValues(p => ({ ...p, 'CX.INCOME.TYPE': v })); onFieldChange && onFieldChange('CX.INCOME.TYPE', v); };

        if (docVal === 'DSCR') {
            if (currentIncome !== 'DSCR') setIncomeValue('DSCR');
            return;
        }

        if (initialDocSyncRef.current) {
            initialDocSyncRef.current = false;
            if (!docVal) { if (currentIncome) setIncomeValue(''); return; }
            if (currentIncome) { if (!allowed.includes(currentIncome)) setIncomeValue(allowed[0] || ''); return; }
            if (!currentIncome && allowed.length > 0) setIncomeValue(allowed[0] || '');
            return;
        }

        if (!docVal) { if (currentIncome) setIncomeValue(''); return; }

        if (allowed.length > 0 && (!currentIncome || !allowed.includes(currentIncome))) setIncomeValue('');
    }, [localFieldValues['CX.DOC.TYPE']]);

    const removeHighlightForField = (fieldId) => {
        const el = document.getElementById(`field-${fieldId}`);
        if (!el) return;
        if (el.hasAttribute('data-required-missing-input')) {
            el.removeAttribute('data-required-missing-input');
            el.style.border = '';
        }
    };

    const handleIntegerInput = (rawValue, field) => {
        // remove highlight for integer field as user is editing
        removeHighlightForField(field.id);

        // allow only digits
        const newValue = rawValue.replace(/[^\d]/g, '');
        handleChange({ target: { value: newValue } }, field);
    };

    // ======================================================
    // Auto-fill & lock field 389 when conditions are met
    // ======================================================
    useEffect(() => {
        if (!bpcPrograms || bpcPrograms.length === 0) return;

        const loanProd = localFieldValues["CX.LOAN.PROD.BROKER.SELECT"];
        const lcp = localFieldValues["LCP.X1"];

        const borrowerPaid = typeof lcp === "string" && /borrower/i.test(lcp);

        const matchRequiredProd = isLoanProgram(loanProd, [
            LOAN_PROGRAM_CODES.PROGRAM_004, // ACTIVATOR / SUNNY
            LOAN_PROGRAM_CODES.PROGRAM_006, // ACTIVATOR ITIN / SUNNY ITIN
            LOAN_PROGRAM_CODES.PROGRAM_001, // ALLY (ChampsTPO only)
            LOAN_PROGRAM_CODES.PROGRAM_011, // ALLY EQUITY FIRST (ChampsTPO only)
            LOAN_PROGRAM_CODES.PROGRAM_008, // SUPER JUMBO / STARRY
            LOAN_PROGRAM_CODES.PROGRAM_009  // ACTIVATOR PRIME / SUNNY SELECT
        ]);

        if (borrowerPaid && matchRequiredProd) {
            // BPC programs come from the pricing API keyed by the Encompass
            // product name, not by canonical code - translate before matching.
            const encompassProductName = toEncompassLoanProgram(loanProd);
            const program = bpcPrograms.find(p => p.ProgramName === encompassProductName);

            if (program) {
                // Set 389 automatically
                const newVal = program.BPC_Percentage;

                setLocalFieldValues(prev => ({
                    ...prev,
                    "389": newVal
                }));

                if (onFieldChange) {
                    onFieldChange("389", newVal);
                }

                setDisable389(true);
            }
            else {
                setDisable389(false);
            }
        } else {
            setDisable389(false);
        }
    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"], localFieldValues["LCP.X1"], bpcPrograms]);

    useEffect(() => {
        const nmls = fieldValues?.["CX.PARENTORG.NMLS.ID"];

        if (nmls === "2611") {

            // Set LCP.X1 only if not already correct
            if (localFieldValues["LCP.X1"] !== "Borrower Paid") {
                setLocalFieldValues(prev => ({
                    ...prev,
                    "LCP.X1": "Borrower Paid"
                }));

                onFieldChange?.("LCP.X1", "Borrower Paid");
            }

            // Disable the dropdown
            setDisableLcp(true);
        } else {
            setDisableLcp(false);
        }

    }, [fieldValues["CX.PARENTORG.NMLS.ID"]]);

    useEffect(() => {
        const loanProd = localFieldValues["CX.LOAN.PROD.BROKER.SELECT"];

        if (isAllyProduct(loanProd)) {
            if (forceLcpLenderPaid) return;
            forceBorrowerPaidBecauseAllyRef.current = true;

            // If previously forced lender-paid (TX cashout), remove that force now
            setForceLcpLenderPaid(false);

            // Force Borrower Paid
            if (localFieldValues["LCP.X1"] !== "Borrower Paid") {
                setLocalFieldValues(prev => ({
                    ...prev,
                    "LCP.X1": "Borrower Paid"
                }));

                onFieldChange?.("LCP.X1", "Borrower Paid");
            }

            setLoanCompPlan("Borrower Paid");
            setDisableLcpBecauseAlly(true);
        } else {
            forceBorrowerPaidBecauseAllyRef.current = false;
            setDisableLcpBecauseAlly(false);
        }
    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"]]);

    const getRuleSetValue = (fieldDef, values) => {
        const rules = Array.isArray(fieldDef?.optionRules) ? fieldDef.optionRules : [];
        for (const r of rules) {
            if (r?.if && ruleMatches(r.if, values) && r?.then?.setValue != null) {
                return r.then.setValue;
            }
        }
        return null;
    };

    useEffect(() => {
        // whenever driving fields change, enforce any JSON rule-set values
        (additionalFields || []).forEach((f) => {
            const forced = getRuleSetValue(f, localFieldValues);
            if (forced == null) return;

            const current = (localFieldValues[f.id] ?? "").toString();
            if (current === forced) return;

            setLocalFieldValues(prev => ({ ...prev, [f.id]: forced }));
            onFieldChange?.(f.id, forced);
            if (f.id === "LCP.X1") setLoanCompPlan(forced); // optional: keeps borrowerPlan logic synced
        });
    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"]]);

    useEffect(() => {
        const loanProd = (localFieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "")
            .toString()
            .trim()
            .toUpperCase();

        const isRestrictedProduct =
            isAllyProduct(loanProd) ||
            isLoanProgram(loanProd, LOAN_PROGRAM_CODES.PROGRAM_008); // SUPER JUMBO / STARRY

        const currentBuyout = (localFieldValues["4459"] || "").toString();

        if (isRestrictedProduct) {
            // Force default to No
            if (currentBuyout !== "false") {
                setLocalFieldValues(prev => ({
                    ...prev,
                    "4459": "false"
                }));

                onFieldChange?.("4459", "false");
            }
        }

    }, [localFieldValues["CX.LOAN.PROD.BROKER.SELECT"], onFieldChange]);

    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
            <div className="section-header">Additional Fields</div>
            <div className="grid md:grid-cols-4 gap-6 mt-4 px-4 py-4">
                {additionalFields.map((baseField) => {
                    const field =
                        isReadOnly && baseField.id === "1548"
                            ? {
                                ...baseField,
                                control: "textbox",
                                readonly: true
                            }
                            : baseField;

                    const value = localFieldValues[field.id] || "";

                    // determine if this specific field should be disabled because of the LCP forcing
                    const isLcpField = field.id === "LCP.X1";
                    const disabledBecauseOfForce = isLcpField && forceLcpLenderPaid;
                    const validation = getDateValidation(field.id);
                    const todayISO = new Date().toISOString().substring(0, 10);

                    // Evaluate dynamic readonly from optionRules
                    let effectiveReadonly = field.readonly;
                    let effectiveOptions = field.options;
                    if (field.control === "dropdown" || field.control === "textbox") {
                        const eff = getEffectiveOptionsAndReadonly(field, localFieldValues);
                        effectiveReadonly = eff.readonly;
                        effectiveOptions = eff.options;
                    }

                    return (
                        <div key={field.id}>
                            <label className="form-label">
                                {field.name}
                                {((field.required && field.id !== '389') || (field.id === '389' && isBorrowerPlan(loanCompPlan))) && (
                                    <span className="text-red-600 ml-1">*</span>
                                )}
                            </label>

                            {field.control === "textbox" && field.datatype === "date" && (

                                <CalendarInput
                                    value={toHtmlInputDate(localFieldValues[field.id])}
                                    id={`field-${field.id}`}
                                    onChange={(e) => {
                                        const formatted = fromHtmlInputDate(e.target.value);

                                        removeHighlightForField(field.id);
                                        // update local state
                                        setLocalFieldValues((prev) => ({
                                            ...prev,
                                            [field.id]: formatted
                                        }));

                                        if (onFieldChange) onFieldChange(field.id, formatted);
                                    }}
                                    min={validation.noPast ? todayISO : undefined}
                                    max={validation.noFuture ? todayISO : undefined}
                                    disabled={effectiveReadonly}
                                />
                            )}
                            {field.control === "textbox" && field.datatype !== "date" && (
                                <input
                                    type="text"
                                    className="form-input"
                                    id={`field-${field.id}`}
                                    value={value}
                                    onChange={(e) => {
                                        let newValue = e.target.value;

                                        removeHighlightForField(field.id);

                                        if (field.datatype === "integer") {
                                            handleIntegerInput(newValue, field);
                                            return;
                                        }

                                        if (field.datatype === "money") {
                                            newValue = newValue.replace(/[^\d]/g, "");
                                            handleChange({ target: { value: newValue } }, field);
                                            return;
                                        }

                                        if (field.datatype === "decimal") {
                                            if (/^\d*\.?\d*$/.test(newValue)) {
                                                handleChange({ target: { value: newValue } }, field);
                                            }
                                        } else {
                                            handleChange(e, field);
                                        }
                                    }}
                                    readOnly={
                                        effectiveReadonly ||
                                        (field.id === '389' && disable389) ||
                                        (field.id === '389' && !isBorrowerPlan(loanCompPlan))
                                    }
                                />
                            )}
                            {field.id === 'CX.INCOME.TYPE' && (
                                <select
                                    className="form-input w-full text-sm border rounded p-1"
                                    id={`field-${field.id}`}
                                    value={localFieldValues[field.id] || ""}
                                    disabled={effectiveReadonly}
                                    onChange={(e) => {
                                        const newVal = e.target.value;

                                        removeHighlightForField(field.id);
                                        setLocalFieldValues(prev => ({ ...prev, [field.id]: newVal }));
                                        if (onFieldChange) onFieldChange(field.id, newVal);
                                    }}
                                >
                                    <option value="">Select an Option</option>

                                    {(() => {
                                        const product = (localFieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "").toString().trim().toUpperCase();
                                        const currentIncome = (localFieldValues["CX.INCOME.TYPE"] || "").toString().trim();

                                        const OLD_STD = "Bank Statement - Standard";
                                        const OLD_STR = "Bank Statement - Streamline";
                                        const NEW_12 = "Bank Statement - Standard 12 months";
                                        const NEW_24 = "Bank Statement - Standard 24 months";

                                        // Start from your existing list (already in config order)
                                        let opts = (filteredIncomeOptions || []).slice();

                                        if (isLoanProgram(product, LOAN_PROGRAM_CODES.PROGRAM_009)) {
                                            // Activator Prime / Sunny Select: show ONLY new 12/24, keep original order
                                            opts = opts.filter(o => o?.value === NEW_12 || o?.value === NEW_24 || o?.value === OLD_STR);
                                        } else {
                                            // Not Activator Prime:
                                            // - always keep new 12/24
                                            // - keep ONLY the legacy value if the loan currently uses it
                                            // - otherwise hide both legacy values
                                            opts = opts.filter(o => {
                                                const v = o?.value;

                                                if (v === OLD_STD) return currentIncome === OLD_STD;       // show only if current is Standard
                                                // if (v === OLD_STR) return currentIncome === OLD_STR;       // show only if current is Streamline
                                                return true;                                               // keep everything else in order
                                            });
                                        }

                                        return opts.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.text}
                                            </option>
                                        ));
                                    })()}
                                </select>
                            )}

                            {field.control === "dropdown" && field.id !== 'CX.INCOME.TYPE' && (() => {

                                const isOccupancy = field.id === "1811";
                                const isOccupancyLocked = isOccupancy && lockOccupancy;

                                const selectedLoanProduct =
                                    (localFieldValues["CX.LOAN.PROD.BROKER.SELECT"] || "").toString().trim().toUpperCase();

                                const disableLenderFeeBuyout =
                                    field.id === "4459" &&
                                    (isAllyProduct(selectedLoanProduct) ||
                                        isLoanProgram(selectedLoanProduct, LOAN_PROGRAM_CODES.PROGRAM_008)); // SUPER JUMBO / STARRY

                                const isDisabled =
                                    effectiveReadonly ||
                                    isOccupancyLocked ||
                                    (field.id === '389' && !isBorrowerPlan(loanCompPlan)) ||
                                    disabledBecauseOfForce ||
                                    (field.id === '2293' && isAllyProduct(localFieldValues['CX.LOAN.PROD.BROKER.SELECT'])) ||
                                    // Only disable these fields if not correspondent
                                    (!isCorrespondent ? [
                                        (field.id === 'CX.SHORT.TERM.RENTAL'),
                                        (field.id === 'CX.INVESTOR.EXPERIENCE')
                                    ] : []).some(cond => cond) ||
                                    (field.id === "LCP.X1" && disableLcpBecauseAlly) ||
                                    (field.id === "LCP.X1" && disableLcp) ||
                                    (field.id === "LCP.X1" && !hasLoCompHistory) ||
                                    disableLenderFeeBuyout;

                                return (
                                    <div className="relative">
                                        <select
                                            className={`form-input w-full text-sm border rounded p-1 
                                            ${isOccupancyLocked ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}`}
                                            id={`field-${field.id}`}
                                            value={localFieldValues[field.id] || ""}
                                            disabled={isDisabled}
                                            title={isOccupancyLocked ? "Occupancy cannot be modified once saved." : ""}
                                            onChange={(e) => {
                                                const newVal = e.target.value;

                                                removeHighlightForField(field.id);

                                                setLocalFieldValues((prev) => ({
                                                    ...prev,
                                                    [field.id]: newVal
                                                }));

                                                let valueToSend = newVal;
                                                if (field.format === "YN_BOOL") {
                                                    if (newVal === "true") valueToSend = "Y";
                                                    else if (newVal === "false") valueToSend = "N";
                                                }

                                                // if this is the loan compensation plan field, update local state
                                                if (field.id === "LCP.X1") {
                                                    setLoanCompPlan(valueToSend);
                                                }

                                                if (onFieldChange) onFieldChange(field.id, valueToSend);

                                                if (field.id === "CX.LOAN.PROD.BROKER.SELECT" && isAllyProduct(valueToSend)) {
                                                    setLocalFieldValues((prev) => ({
                                                        ...prev,
                                                        "2293": "Not Waived"
                                                    }));
                                                    if (onFieldChange) {
                                                        onFieldChange("2293", "Not Waived");
                                                        if (!isCorrespondent) {
                                                            onFieldChange("CX.SHORT.TERM.RENTAL", "");
                                                            onFieldChange("CX.INVESTOR.EXPERIENCE", "");
                                                        }
                                                    }
                                                }
                                                else if (field.id === "CX.LOAN.PROD.BROKER.SELECT" && !isAllyProduct(valueToSend)) {
                                                    setLocalFieldValues((prev) => ({
                                                        ...prev,
                                                        "2293": ""
                                                    }));
                                                    if (onFieldChange) onFieldChange("2293", "");
                                                }

                                                if (field.id === "CX.LOAN.PROD.BROKER.SELECT" && onFieldChange) {
                                                    onFieldChange(field.id, valueToSend);
                                                }
                                            }}

                                        >
                                            <option value="">Select an Option</option>
                                            {
                                                (() => {
                                                    const eff = getEffectiveOptionsAndReadonly(field, localFieldValues);
                                                    const effectiveOptions = eff.options;

                                                    return (field.format === "YN" || field.format === "YN_BOOL" || field.format === "ValueText")
                                                        ? effectiveOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.text}
                                                            </option>
                                                        ))
                                                        : effectiveOptions.map(opt => (
                                                            // disabled: org-blocked loan program - still shown when it is the saved value
                                                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                                {opt.text}
                                                            </option>
                                                        ));
                                                })()
                                            }
                                        </select>

                                        {/* Lock icon for Occupancy */}
                                        {isOccupancyLocked && (
                                            <span
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                                                title="Occupancy cannot be modified once saved."
                                            >
                                                🔒
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}

                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AdditionalInfoControl;
